/**
 * @author: v_bzhichluo
 * @date: 2025/12/26
 * @desc: CasualGamesHub/CasualGamesStoreRanks - Games Store Ranks (休闲游戏商店排行) 页面
 * 参考 StoreRanks/MobileGlobalRegion 结构，包含 All Top Charts 和 Global Compare 两个 Tab
 */
import React from 'react';
import { useIntl, history } from 'umi';
import classnames from 'classnames';
import MobileStoreRankTop from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/page/alltopcharts';
import GlobalCompareTab from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/page/globalcompare';
import ReuseTabPageTemplate from '@intelligence/v3/components/ReuseTabPageTemplate';
import useUrlSyncActiveId from '@intelligence/v3/_hooks/useUrlSyncActiveId';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

type ICasualGamesStoreRanksProps = IBaseProps;

type TTabId = 'alltopcharts' | 'globalcompare';

interface ITabConfg {
  id: TTabId;
  label: string;
  annotation: string;
  pageLogKey: string;
  downloadLogKey: string;
  condKey?: string;
}

const TAB_IDS: TTabId[] = ['alltopcharts', 'globalcompare'];
const DEFAULT_ACTIVE_TAB_ID: TTabId = 'alltopcharts';
const PAGE_PATH_PREFIX = '/intelligence/casualGamesHub/casualGamesStoreRanks';

const CasualGamesStoreRanks: React.FC<ICasualGamesStoreRanksProps> = (props) => {
  const { style, className } = props;

  const query = history.location.query || {};

  const { formatMessage } = useIntl();

  const [activeId, setActiveId] = useUrlSyncActiveId(TAB_IDS, DEFAULT_ACTIVE_TAB_ID);

  const tabCondKeysFromBackend = React.useMemo(() => {
    try {
      const encoded = query.tab_cond_keys as string;
      if (!encoded) {
        return {};
      }
      return JSON.parse(decodeURIComponent(encoded));
    } catch (error) {
      console.error('[Tab Cond Keys] Parse error:', error);
      return {};
    }
  }, [query.tab_cond_keys]);

  const tabConfigs = React.useMemo(() => {
    const configs: Record<TTabId, ITabConfg> = {
      alltopcharts: {
        id: 'alltopcharts',
        label: formatMessage({ id: 'dbi_store_rank_all_top_charts' }),
        annotation: formatMessage({ id: 'dbi_tab_annotation_casual_st' }),
        pageLogKey: 'casualGamesHubStoreRanksAllTopCharts',
        downloadLogKey: 'casualGamesHubStoreRanksAllTopChartsDownloadClick',
      },
      globalcompare: {
        id: 'globalcompare',
        label: formatMessage({ id: 'dbi_store_rank_global_compare' }),
        annotation: formatMessage({ id: 'dbi_tab_annotation_casual_st' }),
        pageLogKey: 'casualGamesHubStoreRanksGlobalCompare',
        downloadLogKey: 'casualGamesHubStoreRanksGlobalCompareDownloadClick',
      },
    };

    Object.keys(configs).forEach((tabId) => {
      const backendCondKey = (tabCondKeysFromBackend as Record<string, string>)[tabId];
      if (backendCondKey) {
        configs[tabId as keyof typeof configs].condKey = backendCondKey;
      }
    });

    return configs;
  }, [formatMessage, tabCondKeysFromBackend]);

  /**
   * Tab 切换处理函数
   * 更新激活状态并同步 URL 参数，触发筛选器 API 获取对应配置
   */
  const handleTabChange = (tab: { id: string }): void => {
    const tabId = (tab.id as TTabId) || DEFAULT_ACTIVE_TAB_ID;
    if (tabId === activeId) {
      return;
    }

    setActiveId(tabId);
    const currentQuery = history.location.query || {};
    const condKey = tabConfigs[tabId]?.condKey;

    history.push({
      pathname: `${PAGE_PATH_PREFIX}/${tabId}`,
      query: {
        ...currentQuery,
        ...(condKey ? { filter_tag_name: condKey } : {}),
      },
    });
  };

  const tabs = React.useMemo(
    () =>
      Object.values(tabConfigs).map((config) => ({
        id: config.id,
        label: config.label,
        component:
          config.id === 'alltopcharts' ? (
            <MobileStoreRankTop
              pageLogConfigKey={config.pageLogKey}
              downloadLogConfigKey={config.downloadLogKey}
              condKey={config.condKey}
            />
          ) : (
            <GlobalCompareTab
              pageLogConfigKey={config.pageLogKey}
              downloadLogConfigKey={config.downloadLogKey}
              condKey={config.condKey}
            />
          ),
      })),
    [tabConfigs],
  );

  const activeTab = tabs.find((item) => item.id === activeId) || tabs[0];
  const activeConfig = tabConfigs[activeId as TTabId];

  return (
    <ReuseTabPageTemplate
      style={style}
      className={classnames('dbi-reuse-page-casual-games-store-ranks', className)}
      title={formatMessage({ id: 'dbi_reuse_page_title_casual_games_store_ranks' })}
      subTitle={activeConfig?.annotation}
      pageLogKeyMap={{
        alltopcharts: tabConfigs.alltopcharts.pageLogKey,
        globalcompare: tabConfigs.globalcompare.pageLogKey,
      }}
      tabs={tabs}
      activeId={activeId}
      onActive={handleTabChange}
    >
      <MarqueeBanner />
      {activeTab?.component}
    </ReuseTabPageTemplate>
  );
};

export default CasualGamesStoreRanks;
